<template>
    <transition name="fade">
        <div v-show="show" class="datepicker" @click.stop>
            <button type="button" class="datepicker__close" @click="$emit('close')" />
            <div class="datepicker__top">
                <div
                    class="datepicker__month"
                    :class="{ active: showMonthDropdown }"
                    @click="showMonthDropdown = !showMonthDropdown"
                >
                    <div class="datepicker__text">{{ months[month - 1].label }}</div>
                    <transition name="fade">
                        <ul v-if="showMonthDropdown" class="datepicker__dropdown">
                            <li
                                v-for="m in months"
                                :key="m.id"
                                :class="{ active: m.number === month }"
                                class="datepicker__dropdown-item"
                                @click="setMonth(m)"
                            >
                                {{ m.label }}
                            </li>
                        </ul>
                    </transition>
                </div>
                <div
                    class="datepicker__year"
                    :class="{ active: showDropdown }"
                    @click="showDropdown = !showDropdown"
                >
                    <div class="datepicker__text">{{ year }}</div>
                    <transition name="fade">
                        <ul v-if="showDropdown" class="datepicker__dropdown">
                            <li
                                v-for="y in yearsToShow"
                                :key="y"
                                class="datepicker__dropdown-item"
                                :class="{ active: y === year }"
                                @click="setYear(y)"
                            >
                                {{ y }}
                            </li>
                        </ul>
                    </transition>
                </div>
            </div>
            <div class="datepicker__calendar">
                <div class="datepicker__week">
                    <div
                        v-for="weekday in weekdays"
                        :key="weekday.number"
                        class="datepicker__weekday"
                    >
                        {{ weekday.label }}
                    </div>
                </div>
                <div v-for="(week, index) in weeks" :key="'week' + index" class="datepicker__week">
                    <div
                        v-for="(day, index) in week"
                        :key="index + '' + day.number"
                        class="datepicker__day"
                        :class="{
                            'not-month': !day.inMonth,
                            picked: day.selected,
                            'not-available': !day.available
                        }"
                        @click="setDate(day.date)"
                    >
                        {{ day.number }}
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import { format, differenceInMonths } from 'date-fns';

const today = new Date();
const daysInMonths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const MONTH_LABELS = [
    {
        rus: 'Январь',
        eng: 'January'
    },
    {
        rus: 'Февраль',
        eng: 'February'
    },
    {
        rus: 'Март',
        eng: 'March'
    },
    {
        rus: 'Апрель',
        eng: 'April'
    },
    {
        rus: 'Май',
        eng: 'May'
    },
    {
        rus: 'Июнь',
        eng: 'June'
    },
    {
        rus: 'Июль',
        eng: 'July'
    },
    {
        rus: 'Август',
        eng: 'August'
    },
    {
        rus: 'Сентябрь',
        eng: 'September'
    },
    {
        rus: 'Октябрь',
        eng: 'October'
    },
    {
        rus: 'Ноябрь',
        eng: 'November'
    },
    {
        rus: 'Декабрь',
        eng: 'December'
    }
];

const WEEKDAY_LABELS = [
    {
        rus: 'Пн',
        eng: 'Mon'
    },
    {
        rus: 'Вт',
        eng: 'Tue'
    },
    {
        rus: 'Ср',
        eng: 'Wed'
    },
    {
        rus: 'Чт',
        eng: 'Thu'
    },
    {
        rus: 'Пт',
        eng: 'Fri'
    },
    {
        rus: 'Сб',
        eng: 'Sat'
    },
    {
        rus: 'Вс',
        eng: 'Sun'
    }
];
export default {
    name: 'Datepicker',
    props: {
        show: Boolean,
        value: [String, Date],
        minDate: {
            type: [Date, String, Number],
            default: 0
        },
        maxDate: {
            type: [Date, String, Number],
            default: 99999999999999
        },
        format: {
            type: String,
            default: 'yyyy-MM-dd'
        }
    },
    data() {
        return {
            month: today.getMonth() + 1,
            year: today.getFullYear(),
            day: today.getDate(),
            showDropdown: false,
            showMonthDropdown: false
        };
    },
    computed: {
        weekdays() {
            return WEEKDAY_LABELS.map((label, i) => ({
                label: label[this.$i18n.locale],
                number: i + 1
            }));
        },
        months() {
            return MONTH_LABELS.map((label, i) => ({
                label: label[this.$i18n.locale],
                number: i + 1
            }));
        },
        daysInMonth() {
            const isFebruary = this.month === 2;

            if (isFebruary && this.isLeapYear) return 29;
            return daysInMonths[this.month - 1];
        },
        firstWeekdayInMonth() {
            return new Date(this.year, this.month - 1, 1).getDay();
        },
        weeks() {
            const weeks = [];
            let previousMonth = true,
                thisMonth = false,
                nextMonth = false;
            let day = this.previousMonthData.days - this.firstWeekdayInMonth + 2;
            let month = this.previousMonthData.month;
            let year = this.previousMonthData.year;
            for (let w = 1; w <= 6 && !nextMonth; w++) {
                const week = [];
                for (let d = 1; d <= 7; d++) {
                    if (previousMonth && d >= this.firstWeekdayInMonth) {
                        day = 1;
                        month = this.month;
                        year = this.year;
                        previousMonth = false;
                        thisMonth = true;
                    }
                    const dayObj = {
                        number: day,
                        weekday: d,
                        month,
                        year,
                        date: new Date(year, month - 1, day),
                        beforeMonth: previousMonth,
                        afterMonth: nextMonth,
                        inMonth: thisMonth
                    };
                    dayObj.available = this.isDateAvailable(dayObj.date);
                    dayObj.selected = this.isDaySelected(dayObj.date);
                    week.push(dayObj);
                    if (thisMonth && day >= this.daysInMonth) {
                        thisMonth = false;
                        nextMonth = true;
                        day = 1;
                        month = this.nextMonthData.month;
                        year = this.nextMonthData.year;
                    } else {
                        day++;
                    }
                }
                weeks.push(week);
            }
            return weeks;
        },
        isLeapYear() {
            return (this.year % 4 === 0 && this.year && 100 !== 0) || this.year % 400 === 0;
        },
        previousMonthData() {
            if (this.month === 1) {
                return {
                    days: daysInMonths[11],
                    month: 12,
                    year: this.year - 1
                };
            }
            return {
                days: this.month === 3 && this.isLeapYear ? 29 : daysInMonths[this.month - 2],
                month: this.month - 1,
                year: this.year
            };
        },
        nextMonthData() {
            if (this.month === 12) {
                return {
                    days: daysInMonths[0],
                    month: 1,
                    year: this.year + 1
                };
            }
            return {
                days: this.month === 1 && this.isLeapYear ? 29 : daysInMonths[this.month],
                month: this.month + 1,
                year: this.year
            };
        },
        yearsToShow() {
            let years = [];
            for (let i = parseInt(new Date().getFullYear()); i >= 1900; i--) {
                years.push(i);
            }
            return years;
        },
        yearAndMonth() {
            return [this.year, this.month].join('-');
        }
    },
    watch: {
        yearAndMonth(newValue, oldValue) {
            const values = [newValue, oldValue].map(v => new Date([...v.split('-'), 1]));
            const diff = Math.abs(differenceInMonths(...values));

            if (!diff) return;

            const { year, month } = this;
            this.$emit('dateChanged', { diff, year, month });
        }
    },
    created() {
        if (this.value) {
            this.setDate(this.value instanceof Date ? this.value : new Date(this.value));
        }
    },
    methods: {
        nextMonth() {
            const { month, year } = this.nextMonthData;
            this.month = month;
            this.year = year;
        },
        previousMonth() {
            const { month, year } = this.previousMonthData;
            this.month = month;
            this.year = year;
        },
        setMonth({ number: month }) {
            this.month = month;
        },
        setYear(year) {
            this.year = year;
        },
        setDate(date) {
            this.year = date.getFullYear();
            this.month = date.getMonth() + 1;
            this.day = date.getDate();

            this.$emit('dateSelected', this.formatDate(date));
        },
        isDaySelected(date) {
            if (!this.value) return false;
            const d = new Date(this.value);

            return (
                date.getFullYear() === d.getFullYear() &&
                d.getMonth() === date.getMonth() &&
                d.getDate() === date.getDate()
            );
        },
        formatDate(date) {
            return format(new Date(date), this.format);
        },
        isDateAvailable(date) {
            const time = new Date(date).getTime();

            return time > new Date(this.minDate) && time < new Date(this.maxDate);
        }
    }
};
</script>

<style lang="scss" src="./Datepicker.scss"></style>
